import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { useDrop } from 'react-dnd';
import tw, { styled } from 'twin.macro';
import chroma from 'chroma-js';

import { useQuery, useMutation } from '@sopranodesign/pulse-hooks';
import { motion, AnimateSharedLayout } from '@sopranodesign/framer-motion';
import {
  PageWrapper,
  Panel,
  Button,
  StickyFooter,
  PanelSlider,
  useNotification,
  Flex,
  EmptyStatePlaceholder,
  Grid,
  Heading2,
  FilterMenu,
  useFilterReducer,
  Label,
  DateTimePicker,
  ButtonSelect,
  Checkbox,
  Tooltip
} from '@sopranodesign/pulse-components';
import { useAuth, PermissionFilter } from '@sopranodesign/pulse-auth';

import { AllMetrics, Metric } from './Components/index';
import types from '../../Components/Types/index';
import { updateMetrics } from './api/index';
import { MetricRouter } from './Routers/index';

// Relative presets recompute against "now" on every load, so a returning "Last Week" stays rolling.
const PRESET_OPTIONS = [
  {
    value: 'last_hour',
    label: 'Last Hour',
    getRange: () => ({
      start: new Date(dayjs().subtract(1, 'hours')),
      end: new Date(dayjs())
    })
  },
  {
    value: 'last_day',
    label: 'Last Day',
    getRange: () => ({
      start: new Date(dayjs().subtract(1, 'days')),
      end: new Date(dayjs())
    })
  },
  {
    value: 'last_week',
    label: 'Last Week',
    getRange: () => ({
      start: new Date(dayjs().subtract(1, 'weeks')),
      end: new Date(dayjs())
    })
  },
  {
    value: 'last_month',
    label: 'Last Month',
    getRange: () => ({
      start: new Date(dayjs().subtract(1, 'months')),
      end: new Date(dayjs())
    })
  }
];

// Prints only the dashboard, not the app navigation around it.
const printStyles = `
  @media print {
    @page { margin: 12mm; }
    html, body, #root { height: auto !important; overflow: visible !important; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #fff; }
    body * { visibility: hidden; }
    [data-print-area], [data-print-area] * { visibility: visible; }
    [data-print-area] { position: absolute; left: 0; top: 0; width: 100%; }
    [data-print-area] * { box-shadow: none !important; min-width: 0; }
    [data-print-area] canvas { max-width: 100% !important; height: auto !important; }
    [data-print-metric] { border: 1px solid #e5e7eb; break-inside: avoid; page-break-inside: avoid; }
    [data-print-hide] { display: none !important; }
  }
`;

const Placeholder = styled(motion.div)`
  ${tw`rounded-sm`}
  width: 100%;
  height: 100%;
  min-height: 8rem;
  grid-column: span ${props => props.columns} / auto;
  grid-row: auto / span ${props => props.rows};
  border: 1px solid ${props => chroma(props.theme.primary).alpha(0.5)};
  background: ${props => chroma(props.theme.primary).alpha(0.25)};
`;

const Dashboard = () => {
  const { user, hasFeature } = useAuth();

  const filters = useFilterReducer({
    id: `dashboard-${user.id}`,
    initialFilters: {
      preset: 'last_week',
      start: new Date(dayjs().subtract(1, 'weeks')),
      end: new Date(dayjs().add('1', 'hours')),
      compare: false
    },
    options: { persist: 'local' }
  });

  // A saved relative preset (e.g. "Last Week") should stay rolling, not freeze to old dates.
  useEffect(() => {
    if (!filters.wasRestored) {
      return;
    }

    const preset = PRESET_OPTIONS.find(
      ({ value }) => value === filters.form.preset
    );

    if (preset) {
      filters.applyFilters({ ...filters.form, ...preset.getRange() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [compare, setCompare] = useState(true);

  const [metricKeys, setMetricKeys] = useState();

  const [editing, setEditing] = useState(false);

  const [addingNew, setAddingNew] = useState(false);

  const [dragPoint, setDragPoint] = useState(null);

  const [placeholderIndex, setPlaceholderIndex] = useState(null);

  const [draggedMetric, setDraggedMetric] = useState(null);

  const { showSuccess } = useNotification();

  const ref = useRef(null);

  const oldKeys = useRef();

  const { data, isSuccess } = useQuery({
    queryKey: ['metrics']
  });

  useEffect(() => {
    if (isSuccess) {
      setMetricKeys(data.data);
    }
  }, [data, isSuccess]);

  const { mutate } = useMutation({
    mutationFn: updateMetrics,
    onSuccess: () => {
      setEditing(false);
      showSuccess('Successfully updated your metrics.');
    }
  });

  const toggleAdding = () => setAddingNew(adding => !adding);

  const startEditing = () => {
    oldKeys.current = metricKeys;
    setEditing(true);
  };

  const cancelEditing = () => {
    setMetricKeys(oldKeys.current);
    setEditing(false);
  };

  const handleNewStart = ([newStart]) => {
    filters.updateFilters({
      preset: 'custom',
      start: newStart
    });
  };

  const handleNewEnd = ([newEnd]) => {
    filters.updateFilters({
      preset: 'custom',
      end: newEnd
    });
  };

  const handleNewPreset = option => {
    filters.updateFilters({
      preset: option.value,
      ...option.getRange()
    });
  };

  const handleNewCompare = () => {
    setCompare(!compare);
    filters.updateFilters({
      compare: compare
    });
  };

  const handleDelete = id => {
    setMetricKeys(keys => keys.filter(key => key.id !== id));
  };

  const handleNewMetric = item => setMetricKeys(keys => keys.concat(item));

  const handleSubmit = () => {
    mutate(metricKeys.map(({ id }) => id));
  };

  const handleDrop = () => {
    const fromIndex = metricKeys.findIndex(key => key.id === draggedMetric);
    const mutableMetricKeys = [...metricKeys];
    mutableMetricKeys.splice(
      placeholderIndex,
      0,
      mutableMetricKeys.splice(fromIndex, 1)[0]
    );

    setPlaceholderIndex(null);
    setDraggedMetric(null);
    setMetricKeys(mutableMetricKeys);
  };

  const [, drop] = useDrop({
    accept: 'METRIC',
    drop(item) {
      if (!ref.current) {
        return;
      }

      handleDrop(item.id);
    }
  });

  const handleHover = (ref, mousePosition, fromId, toId) => {
    setDraggedMetric(fromId);

    const index = metricKeys.findIndex(key => key.id === toId);
    if (toId === fromId) {
      return setPlaceholderIndex(index);
    }

    const rect = ref.current.getBoundingClientRect();

    const x = mousePosition.x - rect.left;

    const normalisedX = x / rect.width;

    // If on the right hand side of a metric, set the placeholder to show after
    setPlaceholderIndex(normalisedX > dragPoint.x ? index + 1 : index);
  };

  const handleMouseDown = event => {
    // If we aren't editing metrics or d
    if (!editing || dragPoint) {
      return;
    }
    // Get the bounding rectangle of target
    const rect = event.target.getBoundingClientRect();

    // Mouse position
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const normalisedX = x / rect.width;
    const normalisedY = y / rect.height;
    setDragPoint({ x: normalisedX, y: normalisedY });
  };

  drop(ref);

  return (
    <React.Fragment>
      <style>{printStyles}</style>
      <MetricRouter />
      <div data-print-area>
        <PageWrapper
          header="Dashboard"
          subheader="Your chatbot's performance"
          actions={
            <Flex xSpace data-print-hide>
              <FilterMenu
                position={FilterMenu.POSITIONS.LEFT}
                buttonProps={{
                  disabled: !(metricKeys && metricKeys.length !== 0)
                }}
                {...filters.props}
              >
                <Flex col style={{ width: '20rem' }}>
                  <Flex col fat="true" mb>
                    <Label htmlFor="preset">Time range</Label>
                    <ButtonSelect
                      id="preset"
                      aria-label="Time range"
                      options={PRESET_OPTIONS}
                      placeholder="Custom"
                      value={
                        PRESET_OPTIONS.find(
                          ({ value }) => value === filters.filters.preset
                        ) || null
                      }
                      onChange={handleNewPreset}
                    />
                  </Flex>
                  <Flex col fat="true" mb>
                    <Label htmlFor="start">Start</Label>
                    <DateTimePicker
                      id="start"
                      arial-label="Start"
                      date={filters.filters.start}
                      onChange={handleNewStart}
                    />
                  </Flex>
                  <Flex col fat>
                    <Label htmlFor="end">End</Label>
                    <DateTimePicker
                      id="end"
                      arial-label="End"
                      date={filters.filters.end}
                      onChange={handleNewEnd}
                    />
                  </Flex>
                  <Flex fat="true" center mt>
                    <Checkbox
                      checked={filters.filters.compare}
                      onChange={handleNewCompare}
                      label="Compare with previous period"
                    />
                    <Tooltip
                      position={Tooltip.POSITIONS.BOTTOM}
                      tooltip={
                        <Tooltip.Content>
                          When ticked, the metric value for the previous period
                          will appear in brackets.
                        </Tooltip.Content>
                      }
                    >
                      <Tooltip.Icon>?</Tooltip.Icon>
                    </Tooltip>
                  </Flex>
                </Flex>
              </FilterMenu>
              {metricKeys && metricKeys.length !== 0 && !editing && (
                <Button
                  variant="secondary"
                  icon="download"
                  onClick={() => window.print()}
                >
                  Print / Save as PDF
                </Button>
              )}
              {metricKeys && metricKeys.length !== 0 && (
                <PermissionFilter can="edit metrics">
                  <Button
                    variant="primary"
                    {...(editing && { colour: 'danger' })}
                    icon={editing ? null : 'pencilAlt'}
                    onClick={editing ? cancelEditing : startEditing}
                  >
                    {editing ? 'Cancel' : 'Edit Dashboard'}
                  </Button>
                </PermissionFilter>
              )}
            </Flex>
          }
        >
          {!metricKeys && (
            <Grid columns={3}>
              <Panel loading loadingItems={1} />
              <Panel loading loadingItems={1} />
              <Panel loading loadingItems={1} />
              <Panel loading loadingItems={1} />
              <Panel loading loadingItems={1} />
              <Panel loading loadingItems={1} />
            </Grid>
          )}
          {metricKeys && metricKeys.length === 0 && (
            <Panel>
              <Flex col middle>
                <EmptyStatePlaceholder
                  type="dashboard"
                  heading="Select a metric"
                  text="Monitor your automation's performance by building a custom dashboard."
                  helpLink="/docs"
                  helpText="Learn more about metrics."
                  actions={
                    <PermissionFilter can="edit metrics">
                      <Button
                        variant={editing ? 'secondary' : 'primary'}
                        {...(editing && { colour: 'danger' })}
                        icon={editing ? null : 'plus'}
                        onClick={editing ? cancelEditing : startEditing}
                      >
                        {editing ? 'Cancel' : 'Add metric'}
                      </Button>
                    </PermissionFilter>
                  }
                />
              </Flex>
            </Panel>
          )}
          {metricKeys && metricKeys.length !== 0 && (
            <Flex col ySpace>
              <Grid columns={6} ref={ref}>
                <AnimateSharedLayout>
                  {metricKeys.map((metric, i) => {
                    if (
                      metric.name === 'Small talk triggered' &&
                      !hasFeature('small talk')
                    ) {
                      return null;
                    }
                    return (
                      <React.Fragment key={i}>
                        {editing && i === placeholderIndex && (
                          <Placeholder
                            layoutId="drag-placeholder"
                            columns={
                              types[
                                metricKeys.find(
                                  ({ id }) => draggedMetric === id
                                ).type
                              ].columns
                            }
                            rows={
                              types[
                                metricKeys.find(
                                  ({ id }) => draggedMetric === id
                                ).type
                              ].rows
                            }
                          />
                        )}
                        {draggedMetric !== metric.id && (
                          <Metric
                            key={metric.id}
                            metric={metric}
                            filters={filters.form}
                            editing={editing}
                            handleHover={handleHover}
                            handleDelete={handleDelete}
                            handleMouseDown={handleMouseDown}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </AnimateSharedLayout>
              </Grid>
            </Flex>
          )}
        </PageWrapper>
      </div>
      {editing && (
        <StickyFooter>
          <Flex center between>
            <Heading2>Editing your dashboard</Heading2>
            <Flex xSpace>
              <Button
                variant="secondary"
                colour="danger"
                onClick={cancelEditing}
              >
                Cancel
              </Button>
              <Button variant="secondary" onClick={toggleAdding}>
                View metrics
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                Save
              </Button>
            </Flex>
          </Flex>
        </StickyFooter>
      )}
      {addingNew && (
        <PanelSlider width="40%" handleHide={toggleAdding} header="All metrics">
          <AllMetrics
            handleMetricClick={handleNewMetric}
            activeMetrics={metricKeys}
          />
        </PanelSlider>
      )}
    </React.Fragment>
  );
};

export default Dashboard;
